import styled from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { darken } from '@savgroup-front-common/primitive/helpers/cssColor';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { SUPPORTED_PROPERTIES } from '@savgroup-front-common/types';

import { getCustomComponentProperty } from '../../helpers';

const BUTTON_SIZE = 30;
const BUTTON_MARGIN = 4;
const NEW_UI_BUTTON_SIZE = 34;

export const PADDING_BUTTONS = (BUTTON_SIZE + BUTTON_MARGIN) * 2;
const NEW_UI_PADDING_BUTTONS = NEW_UI_BUTTON_SIZE + BUTTON_MARGIN;

interface $TabViewNavProps {
  $hasOverflow: boolean;
  $fluid?: boolean;
  $hasTopPadding?: boolean;
  $componentThemeName?: string;
}

export const $TabViewNav = styled.nav<$TabViewNavProps>`
  user-select: none;
  padding: 0;
  margin: 0;
  list-style: none;
  position: relative;
  padding-top: 0;
  overflow-x: ${({ $hasOverflow }) => ($hasOverflow ? 'auto' : null)};
  &::-webkit-scrollbar {
    height: 0 !important;
  }
  overflow: -moz-scrollbars-none;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;
  scrollbar-width: none;
  height: ${({ $componentThemeName, theme }) =>
    $componentThemeName
      ? getCustomComponentProperty({
          theme,
          componentName: $componentThemeName,
          property: SUPPORTED_PROPERTIES.HEIGHT,
        })
      : null};

  @media ${media.minWidth.lg} {
    padding-top: ${({ $fluid, $hasTopPadding }) =>
      $fluid || !$hasTopPadding ? 0 : spacing(5)};
  }
`;

interface $TabViewSelectorProps {
  $position: number;
  $width: number;
  $totalWidth: number;
  $index: number;
  $componentThemeName?: string;
  $hasErrors?: boolean;
}
export const $TabViewSelector = styled.div<$TabViewSelectorProps>`
  height: ${spacing('xs')};
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  transform: translateX(
      ${({ $position, $index, $componentThemeName, theme }) => {
        if ($componentThemeName) {
          return rem(
            $position +
              $index *
                (getCustomComponentProperty({
                  theme,
                  componentName: $componentThemeName,
                  property: SUPPORTED_PROPERTIES.MARGIN,
                }) as any),
          );
        }

        return rem($position);
      }}
    )
    scaleX(
      ${({ $width, $totalWidth }) => {
        if ($totalWidth === 0) {
          return 0;
        }

        return $width / $totalWidth;
      }}
    );

  background-color: ${({ theme, $hasErrors }) => {
    if ($hasErrors) {
      return theme.colors.danger;
    }

    return theme.newUI.variationColors?.light1;
  }};
  transition-duration: 0.3s;
  transform-origin: left;
`;

interface $TabViewListProps {
  $componentThemeName?: string;
  $fluid?: boolean;
}

export const $TabViewList = styled.ul<$TabViewListProps>`
  display: flex;
  align-items: flex-end;
  padding: 0;
  margin: 0;
  list-style: none;
  gap: ${spacing(7)};
  padding-right: ${({ $fluid }) => ($fluid ? 0 : rem(PADDING_BUTTONS))};

  li {
    margin: 0;
    &:not(:nth-last-child(-n + 2)) {
      margin-right: ${({ $componentThemeName, theme }) => {
        if ($componentThemeName) {
          return rem(
            getCustomComponentProperty({
              theme,
              componentName: $componentThemeName,
              property: SUPPORTED_PROPERTIES.MARGIN,
            }) as any,
          );
        }

        return null;
      }};
    }
  }
`;
export const $TabContainer = styled.div<{
  $fluid?: boolean;
  $hasBottomBorder?: boolean;
  $isButtonScrollerVisible?: boolean;
}>`
  padding-left: ${({ $isButtonScrollerVisible }) =>
    $isButtonScrollerVisible ? rem(NEW_UI_PADDING_BUTTONS) : undefined};
  padding-right: ${({ $fluid }) => {
    if ($fluid) {
      return 0;
    }

    return rem(NEW_UI_PADDING_BUTTONS);
  }};
  position: relative;
  border-bottom: ${({ $hasBottomBorder, theme }) => {
    if ($hasBottomBorder) {
      return `1px solid ${theme.colors.lightGrey}`;
    }

    return `1px solid ${theme.newUI.inputBorderColors.default}`;
  }};
  width: 100%;
`;

export const $ButtonScroller = styled.button<{
  $componentThemeName?: string;
}>`
  position: absolute;
  bottom: ${({ $componentThemeName, theme }) => {
    if ($componentThemeName) {
      return getCustomComponentProperty({
        theme,
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.MARGIN_BOTTOM,
      });
    }

    return '7px';
  }};
  &:not(:last-child) {
    left: 0;
  }
  &:last-child {
    right: 0;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.tw.radius.sm};
  svg {
    color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  }
  &:hover {
    svg {
      color: ${({ theme }) =>
        darken(0.2, theme.newUI.defaultColors.secondaryText)};
    }
  }
`;

export const $TabItemWrapper = styled.div<{
  $fluid?: boolean;
}>`
  white-space: nowrap;
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
  transition: 0.3s;
`;

export const $ScrollDetector = styled.div`
  visibility: hidden;
  width: 0;
`;
